import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Select, DatePicker, Space, message, Upload, Tag, Spin, Drawer, Switch, Popconfirm } from 'antd';
import { UploadOutlined, DownloadOutlined, EyeOutlined, TableOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import ItemDetailDrawer from '../components/ItemDetailDrawer';
import StatusDropdown from '../components/StatusDropdown';
import ExpirationDate from '../components/ExpirationDate';
import { documentService, bulkDeleteDocuments } from '../services/documents';
import { documentTypeService } from '../services/documentTypes';
import { itemService } from '../services/items';
import { tenantService } from '../services/tenants';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { downloadXlsx } from '../utils/excelExport';
import './Dashboard.css';

const { Title } = Typography;
const { Option } = Select;

function Documents() {
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
  // Tutti gli hook PRIMA di qualsiasi logica
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [data, setData] = useState([]);
  const [items, setItems] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [search, setSearch] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [documentDetailDrawerOpen, setDocumentDetailDrawerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [tableSort, setTableSort] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { selectedTenantId } = useTenant();
  const { user } = useAuth();
  const [tenantMap, setTenantMap] = useState({});
  const location = useLocation();
  const [initialStateApplied, setInitialStateApplied] = useState(false);
  const isDocumentReadOnly = user?.role === 'all_external';

  // Solo DOPO gli hook, logica bulk delete
  const canBulkDelete = user?.role === 'admin';
  const canCreateDocument = !isDocumentReadOnly;
  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: 'Confermi la rimozione dei documenti selezionati?',
      content: 'Questa operazione eliminerà definitivamente i documenti e tutti i file associati. Non è possibile annullare.',
      okType: 'danger',
      okText: 'Elimina',
      cancelText: 'Annulla',
      onOk: async () => {
        setBulkDeleteLoading(true);
        try {
          await bulkDeleteDocuments(selectedRowKeys);
          message.success('Documenti eliminati con successo');
          setSelectedRowKeys([]);
          loadDocuments();
        } catch (e) {
          message.error('Errore eliminazione documenti');
        } finally {
          setBulkDeleteLoading(false);
        }
      },
    });
  };

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      params.limit = 5000; // carica più risultati per evitare tronchi
      const res = await documentService.list(params);
      setData(res || []);
    } catch (e) {
      message.error('Errore nel caricamento documenti');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const loadItems = useCallback(async () => {
    try {
      const params = {};
      if (selectedTenantId) params.tenant_id = selectedTenantId;
      const res = await itemService.list(null, params);
      setItems(res || []);
    } catch (e) {
      console.error('Errore caricamento item:', e);
    }
  }, [selectedTenantId]);

  const loadDocumentTypes = async () => {
    const canLoadDocumentTypes = user?.role === 'admin' || user?.role === 'qhse_nke';
    if (!canLoadDocumentTypes) {
      setDocumentTypes([]);
      return;
    }

    try {
      const res = await documentTypeService.list({ active_only: true });
      setDocumentTypes(res || []);
    } catch (e) {
      console.error('Errore caricamento tipologie documento:', e);
    }
  };

  const loadTenants = useCallback(async () => {
    const canLoadTenants = user?.role === 'admin' || user?.role === 'qhse_nke' || user?.role === 'all_external';
    if (!canLoadTenants) return;
    try {
      const res = await tenantService.list();
      const map = {};
      (res || []).forEach((t) => {
        map[t.id] = t.name;
      });
      setTenantMap(map);
    } catch (e) {
      console.error('Errore caricamento tenant', e);
    }
  }, [user]);

  const getStatusTag = (status) => {
    const statusMap = {
      da_controllare: { color: 'orange', label: 'DA CONTROLLARE' },
      idoneo: { color: 'green', label: 'IDONEO' },
      non_idoneo: { color: 'red', label: 'NON IDONEO' },
      non_applicabile: { color: 'default', label: 'NON APPLICABILE' },
      missing: { color: 'red', label: 'MANCANTE' },
    };
    const config = statusMap[status] || { color: 'default', label: status?.toUpperCase() };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const statusOrder = ['missing', 'da_controllare', 'idoneo', 'non_idoneo', 'non_applicabile'];
  const statusRank = (status) => {
    const idx = statusOrder.indexOf(status || '');
    return idx === -1 ? statusOrder.length : idx;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      da_controllare: 'DA CONTROLLARE',
      idoneo: 'IDONEO',
      non_idoneo: 'NON IDONEO',
      non_applicabile: 'NON APPLICABILE',
      missing: 'MANCANTE',
    };
    return statusMap[status] || (status || '').toUpperCase();
  };

  const getItemTypeName = (itemType) => {
    const typeMap = {
      vehicle: 'Veicolo',
      equipment: 'Attrezzatura',
      chemical: 'Prodotto Chimico',
      worker: 'Lavoratore',
      company: 'Azienda',
    };
    return typeMap[itemType] || itemType;
  };

  const getItemDisplayName = (item) => {
    if (!item) return '-';
    
    const fields = item.standard_fields || {};
    
    switch (item.item_type) {
      case 'vehicle':
        return fields.identifier || `ID ${item.id}`;
      case 'worker':
        return `${fields.first_name || ''} ${fields.last_name || ''}`.trim() || `ID ${item.id}`;
      case 'equipment':
        return fields.serial_number || fields.identifier || fields.name || `ID ${item.id}`;
      case 'chemical':
        return fields.name || fields.identifier || `ID ${item.id}`;
      case 'company':
        // Per company, il nome è nel tenant associato, ma possiamo usare il campo name se presente
        return fields.name || `ID ${item.id}`;
      default:
        return `ID ${item.id}`;
    }
  };

  useEffect(() => {
    const initLoad = async () => {
      await loadDocuments();
      await loadItems();
      await loadDocumentTypes();
      await loadTenants();
    };
    initLoad();
  }, [selectedTenantId, loadDocuments, loadItems, loadTenants]);

  // Applica eventuali filtri/sort passati via navigate dalla dashboard (solo prima render)
  useEffect(() => {
    if (initialStateApplied) return;
    const state = location.state || {};
    const { status, sortField, sortOrder } = state;
    if (status !== undefined) {
      setStatusFilter(status || null);
    }
    if (sortField && sortOrder) {
      setTableSort({ field: sortField, order: sortOrder });
    }
    if (state && (status !== undefined || (sortField && sortOrder))) {
      setInitialStateApplied(true);
    }
  }, [location.state, initialStateApplied]);

  const filteredData = useMemo(() => {
    let filtered = data;
    
    // Apply item type filter first
    if (itemTypeFilter) {
      filtered = filtered.filter(doc => doc.item?.item_type === itemTypeFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(doc => (doc.status || '') === statusFilter);
    }
    
    // Then apply search on filtered results
    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter((doc) => {
        // Search in document_type
        const docTypeMatch = (doc.document_type || '').toLowerCase().includes(term);
        // Search in item display name
        const itemNameMatch = getItemDisplayName(doc.item).toLowerCase().includes(term);
        // Search in expiration_date
        const expirationMatch = doc.expiration_date && doc.expiration_date.toLowerCase().includes(term);
        // Search in status
        const statusMatch = (doc.status || '').toLowerCase().includes(term);
        
        return docTypeMatch || itemNameMatch || expirationMatch || statusMatch;
      });
    }
    
    return filtered;
  }, [data, search, itemTypeFilter, statusFilter]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const dateSorter = (key) => (a, b) => (a[key] ? new Date(a[key]).getTime() : 0) - (b[key] ? new Date(b[key]).getTime() : 0);

  const sortComparators = {
    item_type: (a, b) => {
      const typeA = a.item?.item_type || '';
      const typeB = b.item?.item_type || '';
      return typeA.localeCompare(typeB);
    },
    item_name: (a, b) => {
      const nameA = getItemDisplayName(a.item);
      const nameB = getItemDisplayName(b.item);
      return nameA.localeCompare(nameB);
    },
    document_type: stringSorter('document_type'),
    expiration_date: dateSorter('expiration_date'),
  };

  const sortedData = useMemo(() => {
    if (!tableSort?.order || !tableSort?.field || !sortComparators[tableSort.field]) {
      return filteredData;
    }
    const arr = [...filteredData];
    arr.sort(sortComparators[tableSort.field]);
    if (tableSort.order === 'descend') arr.reverse();
    return arr;
  }, [filteredData, tableSort]);

  const handleExportXlsx = () => {
    const headers = ['Tipo Item', 'Nome Item', 'Tipo documento', 'Obbligatorio', 'Scadenza', 'Stato'];
    const rows = (sortedData || []).map((doc) => [
      getItemTypeName(doc.item?.item_type),
      getItemDisplayName(doc.item),
      doc.document_type || '',
      doc.is_mandatory ? 'Sì' : 'No',
      doc.expiration_date ? dayjs(doc.expiration_date).format('DD/MM/YYYY') : '',
      getStatusLabel(doc.status),
    ]);
    downloadXlsx('documenti.xlsx', headers, rows);
  };

  const handleTableChange = (_, __, sorter) => {
    if (sorter && sorter.order) {
      setTableSort({ field: sorter.field || sorter.columnKey, order: sorter.order });
    } else {
      setTableSort(null);
    }
  };

  const handleRowClick = (record) => {
    setSelectedDocument(record);
    setDocumentDetailDrawerOpen(true);
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        item_id: values.item_id,
        document_type: values.document_type,
        is_mandatory: values.is_mandatory ?? true,
        status: 'missing',
        expiration_date: values.expiration_date ? values.expiration_date.format('YYYY-MM-DD') : null,
        notes: values.notes,
      };

      await documentService.create(payload);
      message.success('Documento creato');
      setModalOpen(false);
      form.resetFields();
      loadDocuments();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleUpload = (doc) => {
    setSelectedDoc(doc);
    setFileList([]);
    setUploadModalOpen(true);
  };

  const handlePreview = async (record, fileOverride = null) => {
    const targetFile = fileOverride || (record.files && record.files[0]);
    if (targetFile) {
      setPreviewFile(targetFile);
      setPreviewLoading(true);
      setPreviewUrl(documentService.getPreviewUrl(targetFile.id));
      setPreviewModalOpen(true);
    } else {
      message.warning('Nessun file disponibile per l\'anteprima');
    }
  };

  const handleDownload = async (record, fileOverride = null) => {
    const targetFile = fileOverride || (record.files && record.files[0]);
    if (targetFile) {
      try {
        setDownloadLoadingId(record.id);
        const blob = await documentService.downloadFile(targetFile.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = targetFile.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (e) {
        message.error('Errore nel download del file');
      } finally {
        setDownloadLoadingId(null);
      }
    } else {
      message.warning('Nessun file disponibile per il download');
    }
  };

  const handleUploadSubmit = async () => {
    if (fileList.length === 0) {
      message.warning('Seleziona un file');
      return;
    }

    try {
      const file = fileList[0].originFileObj || fileList[0];
      await documentService.uploadFile(selectedDoc.id, file);
      message.success('File caricato con successo');
      setUploadModalOpen(false);
      setFileList([]);
      loadDocuments();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const uploadProps = {
    accept: '.pdf',
    beforeUpload: (file) => {
      // Validate PDF
      const isPdf = (file.type === 'application/pdf') || (/\.pdf$/i.test(file.name || ''));
      if (!isPdf) {
        message.error('Solo file PDF sono consentiti');
        return Upload.LIST_IGNORE;
      }
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error(`File troppo grande: ${(file.size / (1024 * 1024)).toFixed(2)}MB. Dimensione massima: 5MB`);
        return Upload.LIST_IGNORE;
      }
      setFileList([{ originFileObj: file, name: file.name }]);
      return false;
    },
    fileList,
    onRemove: () => setFileList([]),
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      await documentService.update(docId, { status: newStatus });
      message.success('Stato aggiornato');
      loadDocuments();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditingDocument(record);
    editForm.setFieldsValue({
      document_type: record.document_type,
      is_mandatory: record.is_mandatory,
      expiration_date: record.expiration_date ? dayjs(record.expiration_date) : null,
      notes: record.notes,
      notify_on_expiration: record.notify_on_expiration !== false,
    });
    setEditModalOpen(true);
  };

  const onEditSubmit = async (values) => {
    try {
      const payload = {
        document_type: values.document_type,
        is_mandatory: values.is_mandatory,
        expiration_date: values.expiration_date ? values.expiration_date.format('YYYY-MM-DD') : null,
        notes: values.notes,
      };

      await documentService.update(editingDocument.id, payload);
      message.success('Documento aggiornato');
      setEditModalOpen(false);
      setEditingDocument(null);
      editForm.resetFields();
      loadDocuments();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const columns = [
    {
      title: '',
      key: 'preview',
      width: 50,
      render: (_, record) => (
        record.files && record.files.length > 0 ? (
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handlePreview(record);
            }}
          />
        ) : null
      ),
    },
    { 
      title: 'Tipo Item', 
      dataIndex: ['item', 'item_type'], 
      key: 'item_type', 
      columnKey: 'item_type',
      sorter: (a, b) => {
        const typeA = a.item?.item_type || '';
        const typeB = b.item?.item_type || '';
        return typeA.localeCompare(typeB);
      }, 
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'item_type' ? tableSort.order : null,
      render: (_, record) => getItemTypeName(record.item?.item_type)
    },
    { 
      title: 'Nome Item', 
      key: 'item_name', 
      columnKey: 'item_name',
      sorter: (a, b) => {
        const nameA = getItemDisplayName(a.item);
        const nameB = getItemDisplayName(b.item);
        return nameA.localeCompare(nameB);
      }, 
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'item_name' ? tableSort.order : null,
      render: (_, record) => getItemDisplayName(record.item)
    },
    { title: 'Tipo documento', dataIndex: 'document_type', key: 'document_type', columnKey: 'document_type', sorter: stringSorter('document_type'), sortDirections: ['ascend', 'descend'], sortOrder: tableSort?.field === 'document_type' ? tableSort.order : null },
    { title: 'Obbligatorio', dataIndex: 'is_mandatory', key: 'is_mandatory', render: (v) => (v ? 'Sì' : 'No') },
    {
      title: 'Scadenza',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      columnKey: 'expiration_date',
      sorter: dateSorter('expiration_date'),
      sortDirections: ['ascend', 'descend'],
      sortOrder: tableSort?.field === 'expiration_date' ? tableSort.order : null,
      render: (v) => (v ? <ExpirationDate date={v} /> : '-'),
    },
    { 
      title: 'Stato', 
      dataIndex: 'status', 
      key: 'status', 
      render: (status, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return getStatusTag(status);
        }
        return (
          <StatusDropdown
            status={status}
            onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
            hasFiles={hasFiles}
            record={record}
          />
        );
      }
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 280,
      render: (_, record) => {
        const hasFiles = record.files && record.files.length > 0;
        if (isDocumentReadOnly) {
          return hasFiles ? (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(record);
              }}
              loading={downloadLoadingId === record.id}
            >
              Scarica
            </Button>
          ) : '-';
        }
        return (
          <Space>
            <Button 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(record);
              }}
            >
              Modifica
            </Button>
            <Button 
              size="small" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleUpload(record);
              }}
              disabled={record.status === 'non_applicabile'}
            >
              Carica
            </Button>
            {hasFiles && (
              <Button 
                size="small" 
                icon={<DownloadOutlined />} 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(record);
                }}
                loading={downloadLoadingId === record.id}
              >
                Scarica
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <MainLayout title="Documenti">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Documenti</Title>
          <Button icon={<TableOutlined />} onClick={handleExportXlsx}>
            Esporta Excel
          </Button>
          <Select
            placeholder="Filtra per tipo item"
            allowClear
            style={{ width: 200 }}
            value={itemTypeFilter}
            onChange={setItemTypeFilter}
          >
            <Option value={null}>Tutti</Option>
            <Option value="worker">Lavoratori</Option>
            <Option value="vehicle">Veicoli</Option>
            <Option value="equipment">Attrezzature</Option>
            <Option value="chemical">Prodotti Chimici</Option>
            <Option value="company">Aziende</Option>
          </Select>
          <Select
            placeholder="Filtra per stato"
            allowClear
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="missing">Mancante</Option>
            <Option value="da_controllare">Da controllare</Option>
            <Option value="idoneo">Idoneo</Option>
            <Option value="non_idoneo">Non idoneo</Option>
            <Option value="non_applicabile">Non applicabile</Option>
          </Select>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Space>
            {canBulkDelete && selectedRowKeys.length > 0 && (
              <Button danger type="primary" onClick={handleBulkDelete}>
                Elimina selezionati ({selectedRowKeys.length})
              </Button>
            )}
            {canCreateDocument && (
              <Button type="primary" size="large" onClick={() => setModalOpen(true)}>
                + Nuovo Documento
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={sortedData}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} documenti`,
          }}
          rowSelection={canBulkDelete ? {
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          } : undefined}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: record.item ? 'pointer' : 'default' },
          })}
        />
        {/* Bottone elimina selezionati solo in alto */}
      </Card>

      <Modal
        title="Anteprima Documento"
        open={previewModalOpen}
        onCancel={() => {
          setPreviewModalOpen(false);
          setPreviewFile(null);
          setPreviewUrl(null);
          setPreviewLoading(false);
        }}
        footer={previewFile ? (
          <Space>
            <Button onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
              Apri in nuova scheda
            </Button>
            <Button type="primary" onClick={() => handleDownload({ id: previewFile.document_id, files: [previewFile] }, previewFile)}>
              Scarica
            </Button>
          </Space>
        ) : null}
        width={900}
        style={{ top: 20 }}
      >
        {previewLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <Spin size="large" tip="Caricamento documento in corso..." />
          </div>
        )}
        {previewUrl && (
          <iframe
            title={previewFile?.filename || 'Anteprima documento'}
            src={previewUrl}
            onLoad={() => setPreviewLoading(false)}
            style={{
              width: '100%',
              height: '80vh',
              border: 'none',
              display: previewLoading ? 'none' : 'block',
            }}
          />
        )}
      </Modal>

      <Modal
        title="Nuovo Documento"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Crea"
        cancelText="Annulla"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="item_id" label="Item associato" rules={[{ required: true, message: 'Seleziona un item' }]}>
            <Select placeholder="Seleziona item" showSearch optionFilterProp="children">
              {items.map((item) => {
                const tenantName = tenantMap[item.tenant_id] || `Tenant ${item.tenant_id}`;
                const itemName = getItemDisplayName(item);
                const itemTypeName = getItemTypeName(item.item_type);
                const displayLabel = `${tenantName} - ${itemTypeName} - ${itemName}`;
                return (
                  <Option key={item.id} value={item.id}>
                    {displayLabel}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item name="document_type" label="Tipo documento" rules={[{ required: true, message: 'Seleziona il tipo documento' }]}>
            <Select placeholder="Seleziona tipo documento" showSearch optionFilterProp="children">
              {documentTypes.map((type) => (
                <Option key={type.id} value={type.name}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="is_mandatory" label="Obbligatorio" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="expiration_date" label="Data di scadenza">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={3} placeholder="Note opzionali" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Carica file - ${selectedDoc?.document_type}`}
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setFileList([]); }}
        onOk={handleUploadSubmit}
        okText="Carica"
        cancelText="Annulla"
      >
        <Upload.Dragger {...uploadProps} maxCount={1}>
          <p style={{ fontSize: 16 }}>
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p style={{ fontSize: 14, fontWeight: 500 }}>Clicca o trascina un file PDF qui</p>
          <p style={{ fontSize: 12, color: '#666' }}>Solo file PDF - Max 5MB (compresso automaticamente)</p>
        </Upload.Dragger>
      </Modal>

      <Modal
        title="Modifica Documento"
        open={editModalOpen}
        onCancel={() => { 
          setEditModalOpen(false); 
          setEditingDocument(null);
          editForm.resetFields(); 
        }}
        onOk={() => editForm.submit()}
        okText="Salva"
        cancelText="Annulla"
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={onEditSubmit}>
          <Form.Item name="document_type" label="Tipo documento" rules={[{ required: true, message: 'Seleziona il tipo documento' }]}>
            <Select placeholder="Seleziona tipo documento">
              {documentTypes.map((dt) => (
                <Option key={dt.id} value={dt.name}>
                  {dt.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="is_mandatory" label="Obbligatorio" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="expiration_date" label="Data di scadenza">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={3} placeholder="Note opzionali" />
          </Form.Item>
          <Form.Item name="notify_on_expiration" label="Notifiche scadenza" valuePropName="checked" initialValue={true}>
            {editingDocument && (editingDocument.files?.length ?? 0) === 0 ? (
              <Switch disabled title="Aggiungi un file per abilitare le notifiche" />
            ) : (
              <Switch />
            )}
          </Form.Item>
        </Form>
      </Modal>

      <ItemDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedItem(null);
          setSelectedItemType(null);
        }}
        item={selectedItem}
        itemType={selectedItemType}
      />

      <Drawer
        title="Dettagli Documento"
        open={documentDetailDrawerOpen}
        onClose={() => {
          setDocumentDetailDrawerOpen(false);
          setSelectedDocument(null);
        }}
        width={720}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setDocumentDetailDrawerOpen(false)}>
              Chiudi
            </Button>
            <Button 
              type="primary" 
              onClick={() => {
                setDocumentDetailDrawerOpen(false);
                handleEdit(selectedDocument);
              }}
            >
              Modifica
            </Button>
          </Space>
        }
      >
        {selectedDocument && (
          <div>
            <Card title="Informazioni Documento" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px' }}>
                  <strong>ID:</strong>
                  <span>{selectedDocument.id}</span>
                  
                  <strong>Tipo Documento:</strong>
                  <span>{selectedDocument.document_type}</span>
                  
                  <strong>Obbligatorio:</strong>
                  <span>{selectedDocument.is_mandatory ? 'Sì' : 'No'}</span>
                  
                  <strong>Data Scadenza:</strong>
                  <span>{selectedDocument.expiration_date ? <ExpirationDate date={selectedDocument.expiration_date} /> : '-'}</span>
                  
                  <strong>Stato:</strong>
                  <span>{getStatusTag(selectedDocument.status)}</span>
                  
                  <strong>Item Collegato:</strong>
                  <span>
                    <Button 
                      type="link" 
                      size="small" 
                      style={{ padding: 0 }}
                      onClick={() => {
                        if (selectedDocument.item) {
                          setSelectedItem(selectedDocument.item);
                          setSelectedItemType(selectedDocument.item?.item_type);
                          setDrawerOpen(true);
                        }
                      }}
                    >
                      {getItemTypeName(selectedDocument.item?.item_type)} - {getItemDisplayName(selectedDocument.item)}
                    </Button>
                  </span>
                  
                  <strong>Note:</strong>
                  <span>{selectedDocument.notes || '-'}</span>
                </div>
              </Space>
            </Card>
            
            <Card title="File Associati" style={{ marginBottom: 16 }}>
              {selectedDocument.files && selectedDocument.files.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedDocument.files.map((file, idx) => {
                    const isLatest = idx === 0;
                    return (
                      <Card key={file.id} size="small" style={{ backgroundColor: '#f5f5f5' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Space align="center">
                              <strong>{file.filename}</strong>
                              <Tag color={isLatest ? 'green' : 'default'}>
                                {isLatest ? 'Ultima versione' : 'Storico'}
                              </Tag>
                            </Space>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              Caricato il {dayjs(file.uploaded_at).format('DD/MM/YYYY HH:mm')}
                            </div>
                          </div>
                          <Space>
                            <Button 
                              size="small" 
                              icon={<EyeOutlined />}
                              onClick={() => handlePreview(selectedDocument, file)}
                            >
                              Anteprima
                            </Button>
                            <Button 
                              size="small" 
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownload(selectedDocument, file)}
                              loading={downloadLoadingId === selectedDocument.id}
                            >
                              Scarica
                            </Button>
                          </Space>
                        </Space>
                      </Card>
                    );
                  })}
                </Space>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Nessun file caricato
                </div>
              )}
              <Button 
                type="dashed" 
                icon={<UploadOutlined />}
                style={{ width: '100%', marginTop: 16 }}
                onClick={() => {
                  setDocumentDetailDrawerOpen(false);
                  handleUpload(selectedDocument);
                }}
              >
                Carica Nuovo File
              </Button>
            </Card>
          </div>
        )}
      </Drawer>
    </MainLayout>
  );
}

export default Documents;
